import React, { useState, useEffect, useContext, useCallback, useMemo } from 'react';
import {
  Tabs,
  Tab,
  List,
  Typography,
  Box,
  Skeleton,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { fetchProfileData, fetchBookingsForDay } from '../../api';
import BookingModal from './BookingModal';
import { AuthContext } from '../../../contexts/AuthContext';
import { useDatePickerStyles } from '../../DatePickerStyles';
import { useHookState } from '../../NotificationApiHook';
import { PlaceContext } from '../../../contexts/PlaceContext';
import Pagination from '@mui/material/Pagination';
import { getDayMonth, getWeekDay, DEFAULT_OPTIONS } from './utils';
import CancelBookingModal from './CancelBookingModal';
import BookingCard from './BookingCard/BookingCard';
import BookingsFilters from './BookingsFilters/BookingsFilters';
import { groupBookings } from './utils';
import CanceledBookingModal from './CanceledBookingModal/CanceledBookingModal';

const Bookings = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [uniqueBookingDays, setUniqueBookingDays] = useState([]);
  const [bookingsForDay, setBookingsForDay] = useState([]);
  const [loadingBookings, setLoadingBookings] = useState(false);
  const [selectedBlocks, setSelectedBlocks] = useState([]);
  const fabVisible = selectedBlocks.length > 0;
  const [currentTime, setCurrentTime] = useState(new Date());
  const [selectedBooking, setSelectedBooking] = useState({});
  const [isModalOpen, setModalOpen] = useState(false);
  const { isLoggedIn, token } = useContext(AuthContext);
  const [loggedIn, setLoggedIn] = useState(false);
  const { styles } = useDatePickerStyles();

  const { notificationState } = useHookState();
  const [expandedPanel, setExpandedPanel] = useState(0);
  const itemsPerPage = 7; // количество элементов на странице
  const [offset, setOffset] = useState(0);
  const [totalDays, setTotalDays] = useState(0);
  const { placeId } = useContext(PlaceContext);
  const [bookingFilter, setBookingFilter] = useState(DEFAULT_OPTIONS);
  const [forceCancelModalOpenCounter, setForceCancelModalOpenCounter] = useState(0);
  const [canceledBookingModalOpen, setCanceledBookingModalOpen] = useState(false);

  const handlePageChange = (event, page) => {
    setOffset(itemsPerPage * (page - 1));
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (token) {
          const data = await fetchProfileData(token, notificationState, offset, placeId);
          setTotalDays(data.day_items);
          const currentDays = data.days;
          console.log('Set currentDays', currentDays);
          setUniqueBookingDays(currentDays);
        } else {
          setUniqueBookingDays([]);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
      }
    };
    fetchData();
  }, [offset, isLoggedIn, currentTime]);

  const handleFetchBookingsForDay = async (day) => {
    setLoadingBookings(true);
    try {
      const bookings = await fetchBookingsForDay(token, day, notificationState, placeId);

      const groupedBookings = groupBookings(bookings);

      setBookingsForDay(groupedBookings);
    } catch (error) {
      console.error('Error fetching bookings for day:', error);
    } finally {
      setLoadingBookings(false);
    }
  };

  useEffect(() => {
    setLoggedIn(isLoggedIn);
    if (token && uniqueBookingDays.length > 0) {
      handleFetchBookingsForDay(uniqueBookingDays[0].day);
    }
  }, [uniqueBookingDays, currentTime]);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
    setSelectedBlocks([]);
    setCurrentTime(new Date());
  };

  const onSuccessCancel = useCallback(() => {
    setSelectedBlocks([]);
    setCurrentTime(new Date());
  }, []);

  const [selectionMode, setSelectionMode] = useState(false);
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    setSelectedBlocks([]);
  };

  const filteredBookingsForDay = useMemo(() => {
    if (bookingFilter.length > 0) {
      const bookingFilterValues = bookingFilter.map((filter) => filter.value);
      return bookingsForDay.filter((booking) =>
        bookingFilterValues.includes(booking.booking_status),
      );
    }
    return bookingsForDay;
  }, [bookingsForDay, bookingFilter]);

  const handleCancelModalClose = () => {
    setSelectedBlocks([]);
  };

  if (!uniqueBookingDays && loggedIn) {
    return (
      <Container sx={{ marginTop: '100px' }}>
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
        <Skeleton height={60} />
      </Container>
    );
  }

  if (!loggedIn) {
    return (
      <Container sx={{ marginTop: '100px' }}>
        <Typography
          variant='h6'
          sx={{ textAlign: 'center', marginBottom: '50px' }}
        >
          Брони недоступны <br /> Войдите в систему
        </Typography>
      </Container>
    );
  }

  const handleOpenDoorClick = (bookingGroup) => {
    setSelectedBooking(bookingGroup);
    setModalOpen(true);
  };

  // Функция для переключения выбранного временного блока
  const toggleBookingGroup = (block) => {
    if (block.booking_status === 'Canceled' && !selectionMode) {
      setSelectedBlocks([block]);
      setCanceledBookingModalOpen(true);
      return;
    }

    if (block.booking_status === 'Success') {
      if (!selectionMode) {
        setSelectedBlocks([block]);
        setForceCancelModalOpenCounter((prev) => prev + 1);
      } else {
        setSelectedBlocks((prevBlocks) => {
          const blockIndex = prevBlocks.findIndex((selectedBlock) => selectedBlock.id === block.id);
          if (blockIndex !== -1) {
            // Если блок уже выбран, снимаем его выбор
            return prevBlocks.filter((selectedBlock) => selectedBlock.id !== block.id);
          } else {
            // Если блок еще не выбран, добавляем его в список выбранных
            return [...prevBlocks, block];
          }
        });
      }
    }
  };

  const handleAccordionChange = async (index) => {
    setExpandedPanel(index === expandedPanel ? -1 : index);
    // Не посылать запрос при закрытии аккордиона
    if (index === expandedPanel) return;
    const selectedDay = uniqueBookingDays[index];
    console.log(selectedDay.day);
    if (selectedDay) {
      handleFetchBookingsForDay(selectedDay.day);
    }
  };

  const handleCloseCanceledBookingDialog = () => {
    setCanceledBookingModalOpen(false);
    setSelectedBlocks([]);
  };

  return (
    <div>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Tabs
          value={activeTab}
          onChange={handleTabChange}
        >
          <Tab label='Мои Брони' />
          <Tab label='Архив' />
        </Tabs>
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'center' }}>
        <Pagination
          count={Math.ceil(totalDays / itemsPerPage)}
          page={Math.ceil(offset / itemsPerPage) + 1}
          onChange={handlePageChange}
          color='primary'
          sx={{
            marginTop: 2,
            display: Math.ceil(totalDays / itemsPerPage) > 1 ? 'block' : 'none',
          }}
        />
      </Box>
      {activeTab === 0 ? (
        <Box
          sx={{
            maxHeight: fabVisible ? 'calc(100svh - 350px)' : 'calc(100svh - 300px)',
            minHeight: fabVisible ? 'calc(100svh - 245px)' : 'calc(100svh - 165px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
            display: 'flex',
            flexDirection: 'column',

            ...styles.scrollbar,
          }}
        >
          <BookingsFilters
            selectionMode={selectionMode}
            setSelectionMode={toggleSelectionMode}
            bookingFilter={bookingFilter}
            setBookingFilter={setBookingFilter}
          />

          <List
            sx={{
              maxHeight: fabVisible ? 'calc(100vh - 275px)' : 'calc(100vh - 190px)',
              ...styles.scrollbar,
            }}
          >
            {uniqueBookingDays.map((day, index) => (
              <Accordion
                key={index}
                expanded={expandedPanel === index}
                onChange={() => handleAccordionChange(index)}
                sx={{
                  '&.MuiAccordion-root': {
                    boxShadow: 'none',
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                  sx={{ paddingLeft: 0 }}
                >
                  <Typography sx={{ textTransform: 'capitalize' }}>
                    {`${getDayMonth(day.day.replace(/"/g, ''))}  ${getWeekDay(
                      day.day.replace(/"/g, ''),
                    )}`}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails sx={{ paddingLeft: 0 }}>
                  {loadingBookings ? (
                    <></>
                  ) : (
                    <List
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 15,
                        justifyContent: 'space-around',
                      }}
                    >
                      {filteredBookingsForDay?.length ? (
                        <>
                          {filteredBookingsForDay?.map((bookingsGroup, index) => (
                            <BookingCard
                              key={bookingsGroup.id}
                              bookingsGroup={bookingsGroup}
                              selected={selectedBlocks.some(
                                (selectedBlock) => selectedBlock.id === bookingsGroup.id,
                              )}
                              toggleSelection={() => toggleBookingGroup(bookingsGroup)}
                              selectionMode={selectionMode}
                              handleOpenDoorClick={() => handleOpenDoorClick(bookingsGroup)}
                              index={index}
                            />
                          ))}
                        </>
                      ) : (
                        <Paper
                          elevation={3}
                          sx={{ p: 2 }}
                        >
                          <Typography
                            variant='h6'
                            sx={{ textAlign: 'center' }}
                          >
                            Бронирования с текущим статусом отсутствуют
                          </Typography>
                          <Typography
                            variant='body1'
                            sx={{ textAlign: 'center' }}
                          >
                            Добавьте варианты в поле статус
                          </Typography>
                        </Paper>
                      )}
                    </List>
                  )}
                </AccordionDetails>
              </Accordion>
            ))}
          </List>
        </Box>
      ) : (
        <div>
          <Typography
            variant='body1'
            sx={{ textAlign: 'center', marginTop: 10 }}
          >
            Нет архивных бронирований
          </Typography>
        </div>
      )}

      <CancelBookingModal
        selectedGroups={selectedBlocks}
        onSuccessCancel={onSuccessCancel}
        forceCancelModalOpenCounter={forceCancelModalOpenCounter}
        handleCancelModalClose={handleCancelModalClose}
      />

      <CanceledBookingModal
        selectedGroups={selectedBlocks}
        isOpen={canceledBookingModalOpen}
        handleCloseCanceledBookingDialog={handleCloseCanceledBookingDialog}
      />

      <BookingModal
        open={isModalOpen}
        onClose={() => setModalOpen(false)}
        booking={selectedBooking}
        token={token}
      />
    </div>
  );
};

export default Bookings;
